import { useCallback, useEffect, useState } from 'react';
import {
  IconDeviceFloppy,
  IconLinkOff,
  IconLoader2,
  IconPencil,
  IconPlus,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import api from '../lib/api';

// Common country codes for phone numbers
const COUNTRY_CODES = [
  { code: '+27', label: 'South Africa (+27)', country: 'ZA' },
  { code: '+1', label: 'United States (+1)', country: 'US' },
  { code: '+44', label: 'United Kingdom (+44)', country: 'GB' },
  { code: '+61', label: 'Australia (+61)', country: 'AU' },
  { code: '+91', label: 'India (+91)', country: 'IN' },
  { code: '+86', label: 'China (+86)', country: 'CN' },
  { code: '+81', label: 'Japan (+81)', country: 'JP' },
  { code: '+49', label: 'Germany (+49)', country: 'DE' },
  { code: '+33', label: 'France (+33)', country: 'FR' },
  { code: '+39', label: 'Italy (+39)', country: 'IT' },
  { code: '+34', label: 'Spain (+34)', country: 'ES' },
  { code: '+55', label: 'Brazil (+55)', country: 'BR' },
  { code: '+52', label: 'Mexico (+52)', country: 'MX' },
  { code: '+7', label: 'Russia (+7)', country: 'RU' },
  { code: '+82', label: 'South Korea (+82)', country: 'KR' },
];

interface Contact {
  id: number;
  monday_id: number | null;
  first_name: string;
  last_name: string;
  email_address: string;
  phone_number: string;
  daily_reports: number;
  monthly_reports: number;
  signatory_contact: number;
  finance_contact: number;
  principal_contact: number;
  operational_contact: number;
  customer_care_contact: number;
  marketing_contact: number;
  it_contact: number;
  created_at: string;
  updated_at: string;
  linked_distributors?: Array<{
    i_customer: number;
    company_name: string;
  }>;
}

interface DistributorContactsDrawerProps {
  open: boolean;
  onClose: () => void;
  distributorId: number | null;
  distributorICustomer: number | null;
  distributorName: string | null;
}

interface ContactFormData {
  first_name: string;
  last_name: string;
  email_address: string;
  phone_number: string;
  daily_reports: boolean;
  monthly_reports: boolean;
  signatory_contact: boolean;
  finance_contact: boolean;
  principal_contact: boolean;
  operational_contact: boolean;
  customer_care_contact: boolean;
  marketing_contact: boolean;
  it_contact: boolean;
}

const emptyFormData: ContactFormData = {
  first_name: '',
  last_name: '',
  email_address: '',
  phone_number: '',
  daily_reports: false,
  monthly_reports: false,
  signatory_contact: false,
  finance_contact: false,
  principal_contact: false,
  operational_contact: false,
  customer_care_contact: false,
  marketing_contact: false,
  it_contact: false,
};

export function DistributorContactsDrawer({
  open,
  onClose,
  distributorId: _distributorId,
  distributorICustomer,
  distributorName,
}: DistributorContactsDrawerProps) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [formData, setFormData] = useState<ContactFormData>(emptyFormData);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [selectedCountryCode, setSelectedCountryCode] = useState(COUNTRY_CODES[0]);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const fetchContacts = useCallback(async () => {
    if (!distributorICustomer) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/distributors/${distributorICustomer}/contacts`);
      setContacts(response.data?.data ?? []);
    } catch (err: any) {
      console.error('Failed to load contacts', err);
      setError(err.response?.data?.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [distributorICustomer]);

  useEffect(() => {
    if (open && distributorICustomer) {
      fetchContacts();
    }
  }, [open, distributorICustomer, fetchContacts]);

  const handleAddContact = () => {
    setSelectedContact(null);
    setFormData(emptyFormData);
    setSelectedCountryCode(COUNTRY_CODES[0]); // Default to South Africa
    setSaveError(null);
    setPhoneError(null);
    setEditDialogOpen(true);
  };

  const validatePhoneNumber = (phone: string): boolean => {
    if (!phone.trim()) {
      // Phone is optional, so empty is valid
      setPhoneError(null);
      return true;
    }

    // Remove spaces and common formatting characters
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');

    // Check if it's all digits
    if (!/^\d+$/.test(cleanPhone)) {
      setPhoneError('Phone number must contain only digits');
      return false;
    }

    // Check length (typical phone numbers are 7-15 digits)
    if (cleanPhone.length < 7 || cleanPhone.length > 15) {
      setPhoneError('Phone number must be between 7 and 15 digits');
      return false;
    }

    setPhoneError(null);
    return true;
  };

  const handlePhoneChange = (value: string) => {
    setFormData({ ...formData, phone_number: value });
    validatePhoneNumber(value);
  };

  const handleEditContact = (contact: Contact) => {
    setSelectedContact(contact);

    // Parse existing phone number to extract country code
    let phoneNumber = contact.phone_number || '';
    let countryCode = COUNTRY_CODES[0]; // Default to South Africa

    if (phoneNumber) {
      const matchedCode = COUNTRY_CODES.find((cc) => phoneNumber.startsWith(cc.code));
      if (matchedCode) {
        countryCode = matchedCode;
        phoneNumber = phoneNumber.substring(matchedCode.code.length);
      }
    }

    setSelectedCountryCode(countryCode);
    setFormData({
      first_name: contact.first_name,
      last_name: contact.last_name,
      email_address: contact.email_address,
      phone_number: phoneNumber,
      daily_reports: Boolean(contact.daily_reports),
      monthly_reports: Boolean(contact.monthly_reports),
      signatory_contact: Boolean(contact.signatory_contact),
      finance_contact: Boolean(contact.finance_contact),
      principal_contact: Boolean(contact.principal_contact),
      operational_contact: Boolean(contact.operational_contact),
      customer_care_contact: Boolean(contact.customer_care_contact),
      marketing_contact: Boolean(contact.marketing_contact),
      it_contact: Boolean(contact.it_contact),
    });
    setSaveError(null);
    setPhoneError(null);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (contact: Contact) => {
    setSelectedContact(contact);
    setDeleteDialogOpen(true);
  };

  const handleSaveContact = async () => {
    // Validate phone if provided
    if (formData.phone_number && !validatePhoneNumber(formData.phone_number)) {
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      // Combine country code with phone number
      const fullPhoneNumber = formData.phone_number
        ? `${selectedCountryCode.code}${formData.phone_number.replace(/[\s\-\(\)]/g, '')}`
        : '';

      const payload = {
        ...formData,
        phone_number: fullPhoneNumber,
      };

      if (selectedContact) {
        // Update existing contact
        await api.patch(`/distributors/contacts/${selectedContact.id}`, payload);
      } else {
        // Create new contact and link to current distributor
        const createResponse = await api.post('/distributors/contacts', payload);
        const newContact = createResponse.data?.data;

        if (newContact && distributorICustomer) {
          // Link the new contact to the current distributor
          await api.post('/distributors/contacts/link', {
            contactId: newContact.id,
            distributorICustomers: [distributorICustomer],
          });
        }
      }

      setEditDialogOpen(false);
      fetchContacts();
    } catch (err: any) {
      console.error('Failed to save contact', err);
      setSaveError(err.response?.data?.message || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContact = async () => {
    if (!selectedContact) return;

    setSaving(true);
    setSaveError(null);

    try {
      await api.delete(`/distributors/contacts/${selectedContact.id}`);
      setDeleteDialogOpen(false);
      setSelectedContact(null);
      fetchContacts();
    } catch (err: any) {
      console.error('Failed to delete contact', err);
      setSaveError(err.response?.data?.message || 'Failed to delete contact');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlinkContact = async (contactId: number) => {
    if (!distributorICustomer) return;

    try {
      await api.delete(`/distributors/contacts/${contactId}/unlink/${distributorICustomer}`);
      fetchContacts();
    } catch (err: any) {
      console.error('Failed to unlink contact', err);
      setError(err.response?.data?.message || 'Failed to unlink contact');
    }
  };

  const getContactRoles = (contact: Contact): string[] => {
    const roles: string[] = [];
    if (contact.principal_contact) roles.push('Principal');
    if (contact.signatory_contact) roles.push('Signatory');
    if (contact.finance_contact) roles.push('Finance');
    if (contact.operational_contact) roles.push('Operational');
    if (contact.customer_care_contact) roles.push('Customer Care');
    if (contact.marketing_contact) roles.push('Marketing');
    if (contact.it_contact) roles.push('IT');
    return roles;
  };

  const getReportTypes = (contact: Contact): string[] => {
    const reports: string[] = [];
    if (contact.daily_reports) reports.push('Daily');
    if (contact.monthly_reports) reports.push('Monthly');
    return reports;
  };

  return (
    <TooltipProvider delay={200}>
      <>
        <Sheet
          open={open}
          onOpenChange={(isOpen) => {
            if (!isOpen) onClose();
          }}
        >
          <SheetContent
            side="right"
            showCloseButton={false}
            className="w-full max-w-full gap-0 border-l p-0 sm:max-w-none sm:w-[90vw] md:w-[85vw]"
          >
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex shrink-0 items-center justify-between px-4 py-3">
                <div>
                  <SheetTitle className="text-base">Contacts</SheetTitle>
                  {distributorName && (
                    <SheetDescription>{distributorName}</SheetDescription>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={onClose}
                  aria-label="Close contacts drawer"
                >
                  <IconX className="size-5" />
                </Button>
              </div>
              <Separator />

              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-muted-foreground text-sm">
                    Manage contacts for this distributor
                  </p>
                  <Button onClick={handleAddContact} className="shrink-0 gap-1.5">
                    <IconPlus className="size-4" />
                    Add Contact
                  </Button>
                </div>

                {loading && (
                  <div className="flex justify-center py-16">
                    <IconLoader2 className="text-muted-foreground size-8 animate-spin" />
                  </div>
                )}

                {!loading && error && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {!loading && !error && contacts.length === 0 && (
                  <div className="rounded-lg border border-border p-8 text-center">
                    <p className="text-muted-foreground">
                      No contacts found for this distributor.
                    </p>
                    <Button
                      variant="outline"
                      onClick={handleAddContact}
                      className="mt-4 gap-1.5"
                    >
                      <IconPlus className="size-4" />
                      Add First Contact
                    </Button>
                  </div>
                )}

                {!loading && !error && contacts.length > 0 && (
                  <>
                    <div className="rounded-lg border border-border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[15%] text-sm">Name</TableHead>
                            <TableHead className="w-[18%] text-sm">Email</TableHead>
                            <TableHead className="w-[12%] text-sm">Phone</TableHead>
                            <TableHead className="w-[20%] text-sm">Roles</TableHead>
                            <TableHead className="w-[12%] text-sm">Reports</TableHead>
                            <TableHead className="w-[10%] text-sm">Linked To</TableHead>
                            <TableHead className="w-[13%] text-right text-sm">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {contacts.map((contact) => (
                            <TableRow key={contact.id}>
                              <TableCell className="text-sm">
                                {contact.first_name} {contact.last_name}
                              </TableCell>
                              <TableCell className="text-sm">{contact.email_address}</TableCell>
                              <TableCell className="text-sm">{contact.phone_number}</TableCell>
                              <TableCell className="text-sm">
                                <div className="flex flex-wrap gap-1">
                                  {getContactRoles(contact).map((role) => (
                                    <Badge key={role} variant="outline" className="h-6 font-normal">
                                      {role}
                                    </Badge>
                                  ))}
                                  {getContactRoles(contact).length === 0 && (
                                    <span className="text-muted-foreground text-xs">No roles</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm">
                                <div className="flex flex-wrap gap-1">
                                  {getReportTypes(contact).map((report) => (
                                    <Badge key={report} variant="default" className="h-6 font-normal">
                                      {report}
                                    </Badge>
                                  ))}
                                  {getReportTypes(contact).length === 0 && (
                                    <span className="text-muted-foreground text-xs">None</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm">
                                {contact.linked_distributors?.length ?? 0} distributor(s)
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-0.5">
                                  <Tooltip>
                                    <TooltipTrigger
                                      render={
                                        <Button
                                          variant="ghost"
                                          size="icon-sm"
                                          onClick={() => handleEditContact(contact)}
                                          aria-label="Edit contact"
                                        />
                                      }
                                    >
                                      <IconPencil className="size-4" />
                                    </TooltipTrigger>
                                    <TooltipContent>Edit contact</TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger
                                      render={
                                        <Button
                                          variant="ghost"
                                          size="icon-sm"
                                          onClick={() => handleDeleteClick(contact)}
                                          aria-label="Delete contact"
                                        />
                                      }
                                    >
                                      <IconTrash className="size-4" />
                                    </TooltipTrigger>
                                    <TooltipContent>Delete contact</TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger
                                      render={
                                        <Button
                                          variant="ghost"
                                          size="icon-sm"
                                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                          onClick={() => handleUnlinkContact(contact.id)}
                                          aria-label="Unlink from this distributor"
                                        />
                                      }
                                    >
                                      <IconLinkOff className="size-4" />
                                    </TooltipTrigger>
                                    <TooltipContent>Unlink from this distributor</TooltipContent>
                                  </Tooltip>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Reference Tables */}
                    <div className="mt-8">
                      <h3 className="mb-4 font-heading text-base font-medium">Reference</h3>

                      {/* Roles Reference Table */}
                      <p className="mb-2 mt-4 text-sm font-medium">Contact Roles</p>
                      <div className="mb-6 rounded-lg border border-border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[20%] bg-muted text-sm">Role</TableHead>
                              <TableHead className="w-[40%] bg-muted text-sm">Description</TableHead>
                              <TableHead className="w-[40%] bg-muted text-sm">
                                Example Communication
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            <TableRow>
                              <TableCell className="align-top text-sm">Signatory</TableCell>
                              <TableCell className="align-top text-sm whitespace-normal">
                                The individual responsible for signing any legal agreements or documents.
                              </TableCell>
                              <TableCell className="align-top text-sm">Manual communication</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="align-top text-sm">Principal</TableCell>
                              <TableCell className="align-top text-sm whitespace-normal">
                                The Principal in the case that the Distributor is a School
                              </TableCell>
                              <TableCell className="align-top text-sm">Manual communication</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="align-top text-sm">Finance</TableCell>
                              <TableCell className="align-top text-sm whitespace-normal">
                                A finance contact that will receive all emails that contain invoices or
                                purchase orders.
                              </TableCell>
                              <TableCell className="align-top text-sm whitespace-normal">
                                Subscription invoices
                                <br />
                                Monthly reports with purchase order
                                <br />
                                Shopify Sales purchase order
                                <br />
                                Shopify Lincense invoices
                                <br />
                                Top Up invoices
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="align-top text-sm">Operational</TableCell>
                              <TableCell className="align-top text-sm whitespace-normal">
                                An operational contact for manual communication.
                              </TableCell>
                              <TableCell className="align-top text-sm whitespace-normal">
                                Manual communication
                                <br />
                                System outages
                                <br />
                                System enhancements
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="align-top text-sm">IT</TableCell>
                              <TableCell className="align-top text-sm whitespace-normal">
                                An IT contact for manual communication.
                              </TableCell>
                              <TableCell className="align-top text-sm whitespace-normal">
                                Manual communication
                                <br />
                                System outages
                                <br />
                                System enhancements
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="align-top text-sm">Marketing</TableCell>
                              <TableCell className="align-top text-sm whitespace-normal">
                                A marketing contact for manual communication.
                              </TableCell>
                              <TableCell className="align-top text-sm">Manual communication</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="align-top text-sm">Customer Care</TableCell>
                              <TableCell className="align-top text-sm whitespace-normal">
                                An customer contact for manual communication.
                              </TableCell>
                              <TableCell className="align-top text-sm whitespace-normal">
                                Manual communication
                                <br />
                                System outages
                                <br />
                                System enhancements
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>

                      {/* Communication Lists Reference Table */}
                      <p className="mb-2 mt-6 text-sm font-medium">Communication Lists</p>
                      <div className="rounded-lg border border-border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[30%] bg-muted text-sm">List</TableHead>
                              <TableHead className="w-[70%] bg-muted text-sm">Communication</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            <TableRow>
                              <TableCell className="align-top text-sm">Daily Reports</TableCell>
                              <TableCell className="align-top text-sm whitespace-normal">
                                Any daily reports at a Distributor level, this excludes SPAR Mobile reports.
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="align-top text-sm">Monthly Reports</TableCell>
                              <TableCell className="align-top text-sm whitespace-normal">
                                Any monthly reports at a Distributor level, this excludes SPAR Mobile
                                reports. This will include the PO for the reports where applicable.
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Edit/Create Contact Dialog */}
        <Dialog
          open={editDialogOpen}
          onOpenChange={(isOpen) => {
            if (!isOpen) setEditDialogOpen(false);
          }}
        >
          <DialogContent
            showCloseButton={false}
            className="flex max-h-[90vh] max-w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[900px]"
          >
            <DialogHeader className="shrink-0 px-6 pt-6 pb-2">
              <DialogTitle>{selectedContact ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
            </DialogHeader>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4">
              {saveError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{saveError}</AlertDescription>
                </Alert>
              )}

              <div className="flex flex-col gap-4 pt-1">
                <div className="flex flex-col gap-4 sm:flex-row">
                  <div className="flex flex-1 flex-col gap-2">
                    <Label htmlFor="contact-first-name">
                      First Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="contact-first-name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-2">
                    <Label htmlFor="contact-last-name">
                      Last Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="contact-last-name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="contact-email">
                    Email Address <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={formData.email_address}
                    onChange={(e) => setFormData({ ...formData, email_address: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <p className="text-muted-foreground text-sm">Phone Number (Optional)</p>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                    <div className="w-full shrink-0 sm:w-[200px]">
                      <Label htmlFor="country-code" className="sr-only">
                        Country
                      </Label>
                      <Select
                        value={selectedCountryCode.code}
                        onValueChange={(value) => {
                          const next = COUNTRY_CODES.find((c) => c.code === value);
                          if (next) setSelectedCountryCode(next);
                        }}
                      >
                        <SelectTrigger id="country-code" size="sm" className="w-full">
                          <SelectValue placeholder="Country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRY_CODES.map((option) => (
                            <SelectItem key={option.code} value={option.code}>
                              <span>{option.country}</span>
                              <span className="text-muted-foreground">{option.code}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <Label htmlFor="contact-phone-number">Number</Label>
                      <div
                        className={cn(
                          'flex h-8 min-w-0 items-center gap-2 rounded-lg border border-input bg-transparent px-2.5 has-[:focus-visible]:border-ring has-[:focus-visible]:ring-3 has-[:focus-visible]:ring-ring/50 dark:bg-input/30',
                          phoneError &&
                            'border-destructive ring-3 ring-destructive/20 dark:border-destructive/50 dark:ring-destructive/40'
                        )}
                      >
                        <span className="text-muted-foreground shrink-0 text-sm tabular-nums">
                          {selectedCountryCode.code}
                        </span>
                        <Input
                          id="contact-phone-number"
                          value={formData.phone_number}
                          onChange={(e) => handlePhoneChange(e.target.value)}
                          placeholder="812345678"
                          className="h-auto min-w-0 flex-1 border-0 bg-transparent px-0 py-0 shadow-none focus-visible:ring-0 dark:bg-transparent"
                          aria-invalid={!!phoneError}
                        />
                      </div>
                      <p
                        className={cn(
                          'text-sm',
                          phoneError ? 'text-destructive' : 'text-muted-foreground'
                        )}
                      >
                        {phoneError || 'Enter number without country code'}
                      </p>
                    </div>
                  </div>
                </div>

                <Separator className="my-1" />
                <p className="text-sm font-medium">Contact Roles</p>

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={formData.principal_contact}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, principal_contact: checked === true })
                      }
                    />
                    <span>Principal Contact</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={formData.signatory_contact}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, signatory_contact: checked === true })
                      }
                    />
                    <span>Signatory Contact</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={formData.finance_contact}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, finance_contact: checked === true })
                      }
                    />
                    <span>Finance Contact</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={formData.operational_contact}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, operational_contact: checked === true })
                      }
                    />
                    <span>Operational Contact</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={formData.customer_care_contact}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, customer_care_contact: checked === true })
                      }
                    />
                    <span>Customer Care</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={formData.marketing_contact}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, marketing_contact: checked === true })
                      }
                    />
                    <span>Marketing Contact</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={formData.it_contact}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, it_contact: checked === true })
                      }
                    />
                    <span>IT Contact</span>
                  </label>
                </div>

                <Separator className="my-1" />
                <p className="text-sm font-medium">Report Subscriptions</p>

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={formData.daily_reports}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, daily_reports: checked === true })
                      }
                    />
                    <span>Daily Reports</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={formData.monthly_reports}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, monthly_reports: checked === true })
                      }
                    />
                    <span>Monthly Reports</span>
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter className="shrink-0 border-t bg-muted/50 px-6 py-4 sm:justify-end">
              <Button
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveContact}
                disabled={
                  saving ||
                  !formData.first_name ||
                  !formData.last_name ||
                  !formData.email_address ||
                  !!phoneError
                }
                className="gap-1.5"
              >
                {saving ? (
                  <IconLoader2 className="size-4 animate-spin" />
                ) : (
                  <IconDeviceFloppy className="size-4" />
                )}
                {selectedContact ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onOpenChange={(isOpen) => {
            if (!isOpen) setDeleteDialogOpen(false);
          }}
        >
          <DialogContent showCloseButton={false}>
            <DialogHeader>
              <DialogTitle>Delete Contact?</DialogTitle>
            </DialogHeader>
            <div className="text-sm">
              <p>
                Are you sure you want to delete{' '}
                <strong>
                  {selectedContact?.first_name} {selectedContact?.last_name}
                </strong>
                ? This will remove the contact and all links to distributors.
              </p>
              {saveError && (
                <Alert variant="destructive" className="mt-4">
                  <AlertDescription>{saveError}</AlertDescription>
                </Alert>
              )}
            </div>
            <DialogFooter className="sm:justify-end">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteContact}
                disabled={saving}
                className="gap-1.5"
              >
                {saving ? (
                  <IconLoader2 className="size-4 animate-spin" />
                ) : (
                  <IconTrash className="size-4" />
                )}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    </TooltipProvider>
  );
}
